import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Group, Loader, Paper, Select, Stack, Table, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useBranches } from '../branches/branchesApi';
import { useStockValuation } from './inventoryReportsApi';

const fmt = (n: number) => new Intl.NumberFormat('ar-PS', { minimumFractionDigits: 2, numberingSystem: 'latn' }).format(n);
const qty = (n: number) => new Intl.NumberFormat('ar-PS', { maximumFractionDigits: 3, numberingSystem: 'latn' }).format(n);

export function ValuationPage() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const [branchId, setBranchId] = useState<string | null>(null);
  const { data, isLoading } = useStockValuation(branchId);
  const { data: branches } = useBranches();

  const branchOptions = (branches ?? []).map((b) => ({ value: b.branchId, label: ar ? b.nameAr : b.nameEn ?? b.nameAr }));
  const rows = data?.rows ?? [];

  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('nav.sections.operations')} title={t('inventory.valuation.title')} subtitle={t('inventory.valuation.subtitle')} />

      <Paper p="md">
        <Group justify="space-between" wrap="wrap" gap="md">
          <Select data={branchOptions} value={branchId} onChange={setBranchId} clearable
            placeholder={t('inventory.valuation.allBranches')} style={{ minWidth: 220 }} />
          <Group gap="xs">
            <Text size="sm" c="var(--wheels-ink-muted)">{t('inventory.valuation.total')}</Text>
            <Badge size="lg" variant="light" color="red" radius="sm" className="font-numeric">₪ {fmt(data?.totalValue ?? 0)}</Badge>
          </Group>
        </Group>
      </Paper>

      {data && data.uncostedItems > 0 && (
        <Alert color="yellow" icon={<IconAlertTriangle size={18} />} variant="light">
          {t('inventory.valuation.uncostedWarning', { count: data.uncostedItems })}
        </Alert>
      )}

      <Paper p={rows.length ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}><Loader size="sm" /></Stack>
        ) : rows.length === 0 ? (
          <EmptyState icon={<IconAlertTriangle size={22} stroke={1.6} />} title={t('inventory.valuation.empty')} description={t('inventory.valuation.emptyBody')} />
        ) : (
          <Table verticalSpacing="sm" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('inventory.valuation.item')}</Table.Th>
                <Table.Th>{t('inventory.valuation.branch')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('inventory.valuation.onHand')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('inventory.valuation.unitCost')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('inventory.valuation.value')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={`${r.branchId}-${r.itemId}`}>
                  <Table.Td>
                    <Text fw={500} c="var(--wheels-ink-strong)">{ar ? r.itemNameAr : r.itemNameEn ?? r.itemNameAr}</Text>
                    <Text size="xs" c="var(--wheels-ink-muted)" className="font-numeric font-mono">{r.itemSku}</Text>
                  </Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)">{ar ? r.branchNameAr : r.branchNameEn ?? r.branchNameAr}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{qty(r.onHand)}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }} c={r.unitCost == null ? 'var(--wheels-red, #B5191D)' : undefined}>
                    {r.unitCost == null ? t('inventory.valuation.noCost') : fmt(r.unitCost)}
                  </Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 600 }}>₪ {fmt(r.value)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}
